import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  LifeBuoy, HelpCircle, Send, CheckCircle, ChevronDown, ChevronRight,
  MessageSquare, Clock, ArrowLeft, RefreshCw, Paperclip, X, FileText, Download
} from 'lucide-react';

// ─── Attachment limits (mirror backend) ───────────────────────────────────────
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPT_TYPES = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.csv,.xlsx,.txt,.json';
const ALLOWED_MIME = new Set([
  'image/png','image/jpeg','image/jpg','image/gif','image/webp','application/pdf',
  'text/csv','application/csv','application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','text/plain','application/json',
]);

// Read a File → { file_name, mime_type, size_bytes, file_data(base64 no prefix) }
function readFileAsAttachment(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve({ file_name: file.name, mime_type: file.type || 'application/octet-stream', size_bytes: file.size, file_data: base64 });
    };
    r.onerror = () => reject(new Error('Read failed'));
    r.readAsDataURL(file);
  });
}

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

const PRIORITIES = [
  { id: 'low',      label: 'Low',      desc: 'General questions, non-urgent',    color: 'var(--green)' },
  { id: 'normal',   label: 'Normal',   desc: 'Something not working as expected', color: 'var(--cyan)'  },
  { id: 'high',     label: 'High',     desc: 'Tool is broken, data is wrong',     color: 'var(--amber)' },
  { id: 'critical', label: 'Critical', desc: 'Complete outage, data loss',        color: 'var(--red)'   },
];

const ISSUE_TYPES = [
  { id: 'login',       label: 'Login Issues',                            icon: '🔐' },
  { id: 'bug',         label: 'Noticed a Bug',                           icon: '🐛' },
  { id: 'feature',     label: 'New Feature Request',                     icon: '✨' },
  { id: 'enhancement', label: 'Missing Functionality / Tool Enhancement', icon: '🔧' },
  { id: 'outage',      label: 'Outage / Service Down',                   icon: '🚨' },
  { id: 'billing',     label: 'Billing / Subscription',                  icon: '💳' },
  { id: 'other',       label: 'Other',                                   icon: '💬' },
];

const STATUS_STYLES = {
  open:        { color: 'var(--red)',        bg: 'color-mix(in srgb, var(--red) 10%, transparent)',   label: 'Open'        },
  in_progress: { color: 'var(--amber)',      bg: 'color-mix(in srgb, var(--amber) 10%, transparent)',   label: 'In Progress' },
  resolved:    { color: 'var(--green)',      bg: 'color-mix(in srgb, var(--green) 8%, transparent)',  label: 'Resolved'    },
  closed:      { color: 'var(--text-muted)', bg: 'rgba(0,0,0,0)',        label: 'Closed'      },
};

const FAQS = [
  { q: "How do I reset my password?",                                     a: "Go to Account Info → Request Password Reset. A reset link will be sent to your registered email." },
  { q: "Why is my Discrepancy Monitor not showing results?",              a: "Make sure you have uploaded reports for all parties (Ad Server + all Demand Partners) before clicking Run Check." },
  { q: "The Crawler is taking too long. What should I do?",              a: "Reduce the bundle count to under 200 for faster results. Also try reducing Concurrency if you are hitting rate limits." },
  { q: "How do I add a new user to my account?",                         a: "Contact us at support@ignitorads.com with the email address. We will add them within 24 hours." },
  { q: "My sellers.json lookup is returning not found for a known seller.", a: "Some SSPs have very large sellers.json files. The tool searches all sellers. Try re-running the lookup." },
];

function fmtDT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:"1px solid var(--border)", borderRadius:"var(--radius-sm)", marginBottom:"8px", overflow:"hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"12px 14px", cursor:"pointer", background:"var(--bg-elevated)" }}>
        {open ? <ChevronDown size={13} style={{ color:"var(--text-muted)", flexShrink:0 }} /> : <ChevronRight size={13} style={{ color:"var(--text-muted)", flexShrink:0 }} />}
        <span style={{ fontSize:"13px", fontWeight:600, color:"var(--text-primary)" }}>{q}</span>
      </div>
      {open && <div style={{ padding:"12px 14px 14px 35px", fontSize:"13px", color:"var(--text-secondary)", lineHeight:1.7 }}>{a}</div>}
    </div>
  );
}

function TicketThread({ ticket, onBack, onRefresh }) {
  const [replyMsg, setReplyMsg]   = useState("");
  const [sending, setSending]     = useState(false);
  const [full, setFull]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]); // [{file_name, mime_type, size_bytes, file_data}]
  const bottomRef                  = useRef(null);
  const fileInputRef               = useRef(null);

  useEffect(() => { load(); }, [ticket.id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [full?.replies]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/account/support-tickets/" + ticket.id);
      setFull(data);
      onRefresh();
    } catch { toast.error("Failed to load ticket"); }
    finally { setLoading(false); }
  }

  async function handleFilePick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    for (const f of files) {
      if (!ALLOWED_MIME.has((f.type || '').toLowerCase())) { toast.error(`"${f.name}" type not allowed`); continue; }
      if (f.size > MAX_FILE_BYTES) { toast.error(`"${f.name}" exceeds 5 MB`); continue; }
      if (pendingFiles.length >= 10) { toast.error('Max 10 files per message'); break; }
      try {
        const att = await readFileAsAttachment(f);
        setPendingFiles(prev => prev.length < 10 ? [...prev, att] : prev);
      } catch { toast.error(`Could not read "${f.name}"`); }
    }
  }
  function removePending(idx) { setPendingFiles(prev => prev.filter((_, i) => i !== idx)); }

  async function sendReply() {
    if (!replyMsg.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      await axios.post("/api/account/support-tickets/" + ticket.id + "/reply", {
        message: replyMsg.trim(),
        attachments: pendingFiles,
      });
      setReplyMsg("");
      setPendingFiles([]);
      await load();
      toast.success("Reply sent");
    } catch (e) { toast.error(e.response?.data?.error || "Failed to send reply"); }
    finally { setSending(false); }
  }

  const status   = STATUS_STYLES[full?.status || ticket.status] || STATUS_STYLES.open;
  const priority = PRIORITIES.find(p => p.id === (full?.priority || ticket.priority));
  const itype    = ISSUE_TYPES.find(t => t.id === (full?.issue_type || ticket.issue_type));

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px" }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}><ArrowLeft size={12} /> Back</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap", marginBottom:"3px" }}>
            <span style={{ fontWeight:700, fontSize:"15px", color:"var(--text-primary)" }}>#{ticket.id} — {ticket.subject}</span>
            <span style={{ fontSize:"10px", fontWeight:700, color:status.color, background:status.bg, border:"1px solid " + status.color + "40", borderRadius:"4px", padding:"2px 7px" }}>{status.label}</span>
            {priority && <span style={{ fontSize:"10px", color:priority.color, fontWeight:600 }}>{priority.label}</span>}
          </div>
          <div style={{ fontSize:"11px", color:"var(--text-muted)", display:"flex", gap:"12px", flexWrap:"wrap" }}>
            {itype && <span>{itype.icon} {itype.label}</span>}
            <span>Opened {fmtDT(ticket.created_at)}</span>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={11} style={{ animation:loading?"spin 1s linear infinite":"none" }} />
        </button>
      </div>

      <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:"var(--radius-lg)", overflow:"hidden", marginBottom:"16px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:"40px" }}><span className="spinner" /></div>
        ) : (
          <div style={{ maxHeight:"480px", overflowY:"auto", padding:"16px", display:"flex", flexDirection:"column", gap:"12px" }}>
            {(full?.replies || []).map((reply, i) => {
              const isAdmin = reply.author_type === "admin";
              return (
                <div key={reply.id || i} style={{ display:"flex", gap:"10px", flexDirection:isAdmin?"row":"row-reverse" }}>
                  <div style={{ width:34, height:34, borderRadius:"50%", flexShrink:0, background:isAdmin?"color-mix(in srgb, var(--cyan) 15%, transparent)":"color-mix(in srgb, var(--purple) 15%, transparent)", border:"1px solid " + (isAdmin?"var(--cyan)":"var(--purple)") + "40", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:800, color:isAdmin?"var(--cyan)":"var(--purple)" }}>
                    {isAdmin ? "IG" : (reply.author_name?.[0] || "C").toUpperCase()}
                  </div>
                  <div style={{ maxWidth:"75%" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"4px", flexDirection:isAdmin?"row":"row-reverse" }}>
                      <span style={{ fontSize:"11px", fontWeight:700, color:isAdmin?"var(--cyan)":"var(--purple)" }}>{isAdmin?"IgnitorAds Support":(reply.author_name||"You")}</span>
                      {isAdmin && <span className="badge badge-cyan" style={{ fontSize:"9px" }}>Support</span>}
                      <span style={{ fontSize:"10px", color:"var(--text-muted)" }}>{fmtDT(reply.created_at)}</span>
                    </div>
                    <div style={{ padding:"10px 14px", borderRadius:isAdmin?"4px 14px 14px 14px":"14px 4px 14px 14px", background:isAdmin?"color-mix(in srgb, var(--cyan) 6%, transparent)":"color-mix(in srgb, var(--purple) 6%, transparent)", border:"1px solid " + (isAdmin?"color-mix(in srgb, var(--cyan) 15%, transparent)":"color-mix(in srgb, var(--purple) 15%, transparent)"), fontSize:"13px", color:"var(--text-primary)", lineHeight:1.7, whiteSpace:"pre-wrap" }}>
                      {reply.message}
                      {(reply.attachments || []).length > 0 && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginTop: reply.message ? "10px" : 0 }}>
                          {reply.attachments.map(att => (
                            <a key={att.id}
                              href={"/api/account/support-tickets/" + ticket.id + "/attachments/" + att.id}
                              target="_blank" rel="noopener noreferrer"
                              title={att.file_name + " · " + fmtBytes(att.size_bytes)}
                              style={{ display:"inline-flex", alignItems:"center", gap:"6px", padding:"5px 9px", borderRadius:"8px", background:"var(--bg-card)", border:"1px solid var(--border)", textDecoration:"none", color:"var(--text-primary)", fontSize:"11px", maxWidth:"200px" }}>
                              <FileText size={12} style={{ color:"var(--cyan)", flexShrink:0 }} />
                              <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{att.file_name}</span>
                              <Download size={11} style={{ color:"var(--text-muted)", flexShrink:0 }} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        {(full?.status === "resolved" || full?.status === "closed") && (
          <div style={{ padding:"12px 16px", background:"color-mix(in srgb, var(--green) 6%, transparent)", borderTop:"1px solid color-mix(in srgb, var(--green) 20%, transparent)", display:"flex", alignItems:"center", gap:"8px", fontSize:"12px", color:"var(--green)" }}>
            <CheckCircle size={13} />
            <span>This ticket is <strong>{full.status}</strong>. Reply below to reopen it if needed.</span>
          </div>
        )}

        <div style={{ padding:"14px 16px", borderTop:"1px solid var(--border)", background:"var(--bg-elevated)" }}>
          <textarea className="input-field" style={{ width:"100%", minHeight:"80px", resize:"vertical", fontSize:"13px", lineHeight:1.6, marginBottom:"8px" }}
            placeholder="Type your reply..."
            value={replyMsg} onChange={e => setReplyMsg(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply(); }} />
          {/* Pending attachment chips */}
          {pendingFiles.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginBottom:"8px" }}>
              {pendingFiles.map((f, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:"6px", padding:"4px 8px", borderRadius:"8px", background:"var(--bg-card)", border:"1px solid var(--border)", fontSize:"11px", maxWidth:"200px" }}>
                  <FileText size={11} style={{ color:"var(--cyan)", flexShrink:0 }} />
                  <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.file_name}</span>
                  <span style={{ color:"var(--text-muted)" }}>{fmtBytes(f.size_bytes)}</span>
                  <button onClick={() => removePending(i)} style={{ background:"none", border:"none", color:"var(--red)", cursor:"pointer", padding:0, display:"flex" }}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple accept={ACCEPT_TYPES} style={{ display:"none" }} onChange={handleFilePick} />
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={sending || pendingFiles.length >= 10}>
                <Paperclip size={11} /> Attach
              </button>
              <span style={{ fontSize:"10px", color:"var(--text-muted)" }}>Ctrl+Enter to send · max 5 MB/file</span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={sendReply} disabled={sending || (!replyMsg.trim() && pendingFiles.length === 0)}>
              {sending ? <><RefreshCw size={10} style={{ animation:"spin 1s linear infinite" }} /> Sending...</> : <><Send size={10} /> Send Reply</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewTicketForm({ onSubmitted }) {
  const [subject,   setSub]   = useState("");
  const [message,   setMsg]   = useState("");
  const [priority,  setPrio]  = useState("normal");
  const [issueType, setIT]    = useState("");
  const [submitting,setSub2]  = useState(false);

  async function handleSubmit() {
    if (!issueType)       { toast.error("Please select an issue type"); return; }
    if (!subject.trim())  { toast.error("Please enter a subject"); return; }
    if (!message.trim())  { toast.error("Please describe the issue"); return; }
    setSub2(true);
    try {
      const { data } = await axios.post("/api/account/support-ticket", { subject, message, priority, issueType });
      toast.success("Ticket #" + data.ticketId + " created — we will respond within 24 hours");
      onSubmitted();
    } catch { toast.error("Failed to submit — please email support@ignitorads.com"); }
    finally { setSub2(false); }
  }

  return (
    <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:"var(--radius-lg)", padding:"24px" }}>
      <div style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"15px", color:"var(--text-primary)", marginBottom:"18px", display:"flex", alignItems:"center", gap:"8px" }}>
        <LifeBuoy size={16} style={{ color:"var(--cyan)" }} /> Raise a Support Ticket
      </div>

      <div style={{ marginBottom:"16px" }}>
        <label style={{ fontSize:"11px", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:"8px", fontWeight:700 }}>Issue Type *</label>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px" }}>
          {ISSUE_TYPES.map(t => (
            <div key={t.id} onClick={() => setIT(t.id)} style={{ padding:"8px 12px", borderRadius:"var(--radius-sm)", cursor:"pointer", border:"1px solid " + (issueType===t.id?"var(--cyan)":"var(--border)"), background:issueType===t.id?"color-mix(in srgb, var(--cyan) 8%, transparent)":"var(--bg-elevated)", display:"flex", alignItems:"center", gap:"8px", transition:"all 0.15s" }}>
              <span style={{ fontSize:"15px" }}>{t.icon}</span>
              <span style={{ fontSize:"12px", fontWeight:issueType===t.id?700:400, color:issueType===t.id?"var(--cyan)":"var(--text-primary)" }}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom:"16px" }}>
        <label style={{ fontSize:"11px", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:"8px", fontWeight:700 }}>Priority</label>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px" }}>
          {PRIORITIES.map(p => (
            <div key={p.id} onClick={() => setPrio(p.id)} style={{ padding:"8px 12px", borderRadius:"var(--radius-sm)", cursor:"pointer", border:"1px solid " + (priority===p.id?p.color:"var(--border)"), background:priority===p.id?p.color+"15":"var(--bg-elevated)", transition:"all 0.15s" }}>
              <div style={{ fontSize:"12px", fontWeight:700, color:priority===p.id?p.color:"var(--text-primary)" }}>{p.label}</div>
              <div style={{ fontSize:"10px", color:"var(--text-muted)" }}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom:"12px" }}>
        <label style={{ fontSize:"11px", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:"6px", fontWeight:700 }}>Subject *</label>
        <input className="input-field w-full" placeholder="Brief description of the issue" value={subject} onChange={e => setSub(e.target.value)} />
      </div>

      <div style={{ marginBottom:"18px" }}>
        <label style={{ fontSize:"11px", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:"6px", fontWeight:700 }}>Describe the Issue *</label>
        <textarea className="input-field" style={{ width:"100%", minHeight:"120px", resize:"vertical", lineHeight:1.6 }}
          placeholder="What happened? What were you trying to do? Include any error messages, tool names, and steps to reproduce."
          value={message} onChange={e => setMsg(e.target.value)} />
      </div>

      <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting||!subject.trim()||!message.trim()||!issueType} style={{ width:"100%", justifyContent:"center" }}>
        {submitting ? "Submitting..." : <><Send size={13} /> Submit Ticket</>}
      </button>
    </div>
  );
}

function TicketList({ tickets, loading, onSelect }) {
  if (loading) return <div style={{ textAlign:"center", padding:"40px" }}><span className="spinner" /></div>;
  if (!tickets.length) return (
    <div style={{ textAlign:"center", padding:"40px", color:"var(--text-muted)" }}>
      <MessageSquare size={28} style={{ marginBottom:"12px", opacity:0.4 }} />
      <div style={{ fontSize:"13px" }}>No tickets yet. Raise one using the form on the left.</div>
    </div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      {tickets.map(t => {
        const status   = STATUS_STYLES[t.status] || STATUS_STYLES.open;
        const priority = PRIORITIES.find(p => p.id === t.priority);
        const itype    = ISSUE_TYPES.find(i => i.id === t.issue_type);
        const hasUnread = parseInt(t.unread_count) > 0;
        return (
          <div key={t.id} onClick={() => onSelect(t)} style={{ padding:"12px 16px", background:"var(--bg-card)", border:"1px solid " + (hasUnread?"var(--cyan)":"var(--border)"), borderRadius:"var(--radius)", cursor:"pointer", transition:"border-color 0.15s", display:"flex", alignItems:"center", gap:"12px" }}>
            <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0, background:hasUnread?"var(--cyan)":"transparent", border:hasUnread?"none":"1px solid var(--border)" }} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap", marginBottom:"3px" }}>
                <span style={{ fontWeight:hasUnread?700:600, fontSize:"13px", color:"var(--text-primary)" }}>#{t.id} — {t.subject}</span>
                {hasUnread && <span className="badge badge-cyan" style={{ fontSize:"9px" }}>{t.unread_count} new</span>}
              </div>
              <div style={{ display:"flex", gap:"10px", fontSize:"11px", color:"var(--text-muted)", flexWrap:"wrap" }}>
                {itype && <span>{itype.icon} {itype.label}</span>}
                {priority && <span style={{ color:priority.color }}>● {priority.label}</span>}
                <span>{fmtDT(t.created_at)}</span>
                <span>{t.reply_count} message{t.reply_count !== "1" ? "s" : ""}</span>
              </div>
            </div>
            <span style={{ fontSize:"10px", fontWeight:700, color:status.color, background:status.bg, border:"1px solid " + status.color + "40", borderRadius:"4px", padding:"3px 8px", whiteSpace:"nowrap" }}>{status.label}</span>
            <ChevronRight size={14} style={{ color:"var(--text-muted)", flexShrink:0 }} />
          </div>
        );
      })}
    </div>
  );
}

export default function SupportPage() {
  const [view, setView]         = useState("list");
  const [tickets, setTickets]   = useState([]);
  const [ticketsLoading, setTL] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadTickets(); }, []);

  async function loadTickets() {
    setTL(true);
    try { const { data } = await axios.get("/api/account/support-tickets"); setTickets(data); }
    catch {}
    finally { setTL(false); }
  }

  if (view === "thread" && selected) return (
    <div>
      <h1 className="page-title">Support</h1>
      <TicketThread ticket={selected} onBack={() => { setView("list"); loadTickets(); }} onRefresh={loadTickets} />
    </div>
  );

  return (
    <div>
      <h1 className="page-title">Support</h1>
      <p className="page-subtitle" style={{ marginBottom:"20px" }}>
        Raise a ticket and our team will get back to you. For critical issues, email{" "}
        <span style={{ color:"var(--cyan)" }}>support@ignitorads.com</span> directly.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"20px" }}>
        <div>
          <div style={{ display:"flex", gap:"4px", marginBottom:"14px" }}>
            <button className={"btn btn-sm " + (view==="list"?"btn-primary":"btn-secondary")} onClick={() => setView("list")}>
              <MessageSquare size={12} /> My Tickets {tickets.length>0 && "("+tickets.length+")"}
            </button>
            <button className={"btn btn-sm " + (view==="new"?"btn-primary":"btn-secondary")} onClick={() => setView("new")}>
              <LifeBuoy size={12} /> Raise a Ticket
            </button>
          </div>
          {view === "new"
            ? <NewTicketForm onSubmitted={() => { loadTickets(); setView("list"); }} />
            : <TicketList tickets={tickets} loading={ticketsLoading} onSelect={t => { setSelected(t); setView("thread"); }} />}
        </div>
        <div>
          <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:"var(--radius-lg)", padding:"24px" }}>
            <div style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"15px", color:"var(--text-primary)", marginBottom:"16px", display:"flex", alignItems:"center", gap:"8px" }}>
              <HelpCircle size={16} style={{ color:"var(--purple)" }} /> Frequently Asked Questions
            </div>
            {FAQS.map((faq, i) => <FAQ key={i} {...faq} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
